<template>
  <nav class="archive-nav" aria-label="Archive navigation">
    <div class="nav-content">
      <!-- Row 1: Wing tabs + Personal links -->
      <div class="nav-row-primary">
        <div class="wing-tabs" role="navigation" aria-label="Wings quick navigation">
          <router-link
            to="/"
            class="wing-tab"
            active-class=""
            exact-active-class="router-link-active"
            aria-label="Navigate to Collection"
          >
            Collection
          </router-link>
          <router-link to="/wing/geography" class="wing-tab" aria-label="Navigate to Geography Wing">
            Geography
          </router-link>
          <router-link to="/wing/technology" class="wing-tab" aria-label="Navigate to Technology Wing">
            Technology
          </router-link>
          <router-link to="/wing/temporal" class="wing-tab" aria-label="Navigate to Temporal Wing">
            Temporal
          </router-link>
          <router-link to="/wing/infrastructure" class="wing-tab" aria-label="Navigate to Infrastructure Wing">
            Infrastructure
          </router-link>
          <router-link to="/wing/digitalVisitors" class="wing-tab" aria-label="Navigate to Digital Visitors Wing">
            Digital Visitors
          </router-link>
        </div>

        <div class="nav-divider"></div>

        <div class="personal-links">
          <router-link to="/your-visit" class="personal-link" aria-label="Navigate to Your Visit">
            Your Visit
          </router-link>
          <router-link to="/achievements" class="personal-link achievements-link" aria-label="Navigate to Achievements">
            Achievements
          </router-link>
        </div>
      </div>

      <!-- Row 2: Time filter + Stats (contextual) -->
      <div v-if="showContextualRow" class="nav-row-secondary">
        <div class="time-period-filter" role="group" aria-label="Time period filter">
          <span class="filter-label" id="period-filter-label">Period:</span>
          <!-- Desktop buttons -->
          <div class="period-buttons desktop-only" role="radiogroup" aria-labelledby="period-filter-label">
            <button
              v-for="period in periods"
              :key="period.value"
              :class="['period-button', { active: currentPeriod === period.value }]"
              role="radio"
              :aria-checked="currentPeriod === period.value"
              :aria-label="`Filter by ${period.label}`"
              @click="changePeriod(period.value)"
            >
              {{ period.label }}
            </button>
          </div>
          <!-- Mobile select -->
          <select
            class="period-select mobile-only"
            :value="currentPeriod"
            @change="changePeriod(($event.target as HTMLSelectElement).value as TimePeriod)"
            aria-label="Select time period"
          >
            <option v-for="period in periods" :key="period.value" :value="period.value">
              {{ period.label }}
            </option>
          </select>
        </div>

        <div class="stats-summary" role="region" aria-label="Collection statistics">
          <router-link to="/" class="stat-item" aria-label="View collection">
            <span class="stat-value" aria-label="Total specimens count">{{ totalSpecimens }}</span>
            <span class="stat-label">Specimens</span>
          </router-link>
          <router-link to="/achievements" class="stat-item" aria-label="View achievements">
            <span class="stat-value" aria-label="Total achievements count">{{ totalAchievements }}</span>
            <span class="stat-label">Achievements</span>
          </router-link>
        </div>
      </div>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import type { TimePeriod } from '@ephemera/shared';
import { useArchiveStore } from '../../stores/archive';

const archiveStore = useArchiveStore();
const route = useRoute();

const periods = [
  { value: 'week' as TimePeriod, label: 'Week' },
  { value: 'month' as TimePeriod, label: 'Month' },
  { value: 'quarter' as TimePeriod, label: 'Quarter' },
  { value: 'year' as TimePeriod, label: 'Year' },
  { value: 'all' as TimePeriod, label: 'All Time' },
];

const currentPeriod = computed(() => archiveStore.currentPeriod);
const totalSpecimens = computed(() => archiveStore.totalSpecimens);
const totalAchievements = computed(() => archiveStore.totalAchievements);

// Hide time filter + stats on non-collection pages
const showContextualRow = computed(() => {
  const path = route.path;
  return path === '/' || path.startsWith('/wing/');
});

function changePeriod(period: TimePeriod) {
  archiveStore.changePeriod(period);
}
</script>

<style scoped>
.archive-nav {
  background-color: var(--color-sepia-medium);
  border-bottom: 1px solid var(--color-black-primary);
  padding: 0 var(--spacing-xl);
}

.nav-content {
  max-width: 1400px;
  margin: 0 auto;
}

/* Row 1: Wings + Personal */
.nav-row-primary {
  display: flex;
  align-items: stretch;
  gap: 0;
}

.wing-tabs {
  display: flex;
  gap: 0;
  flex: 1;
}

.wing-tab {
  padding: var(--spacing-md) var(--spacing-lg);
  color: var(--color-black-primary);
  text-decoration: none;
  font-size: 0.875rem;
  font-weight: 500;
  border-bottom: 3px solid transparent;
  transition: all var(--duration-fast) ease;
  white-space: nowrap;
}

.wing-tab:hover,
.wing-tab:focus {
  background-color: rgba(0, 0, 0, 0.04);
  border-bottom-color: var(--color-sepia-medium);
}

.wing-tab:focus {
  outline: 2px solid var(--color-burgundy);
  outline-offset: -2px;
}

.wing-tab.router-link-active {
  border-bottom-color: var(--color-burgundy);
  color: var(--color-burgundy);
  font-weight: 600;
}

.nav-divider {
  width: 1px;
  background: var(--color-black-primary);
  opacity: 0.15;
  margin: var(--spacing-sm) 0;
}

.personal-links {
  display: flex;
  gap: 0;
}

.personal-link {
  padding: var(--spacing-md) var(--spacing-lg);
  color: var(--color-black-secondary);
  text-decoration: none;
  font-size: 0.875rem;
  font-weight: 500;
  border-bottom: 3px solid transparent;
  transition: all var(--duration-fast) ease;
  white-space: nowrap;
}

.personal-link:hover,
.personal-link:focus {
  background-color: rgba(0, 0, 0, 0.04);
}

.personal-link:focus {
  outline: 2px solid var(--color-burgundy);
  outline-offset: -2px;
}

.personal-link.router-link-active {
  border-bottom-color: var(--color-burgundy);
  color: var(--color-burgundy);
  font-weight: 600;
}

.achievements-link {
  color: var(--color-legendary);
}

.achievements-link.router-link-active {
  color: var(--color-burgundy);
}

/* Row 2: Time filter + Stats */
.nav-row-secondary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-sm) 0;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.time-period-filter {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.filter-label {
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--color-text-muted);
}

.period-buttons {
  display: flex;
  gap: var(--spacing-xs);
}

.period-button {
  padding: var(--spacing-xs) var(--spacing-md);
  background-color: transparent;
  color: var(--color-black-secondary);
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all var(--duration-fast) ease;
}

.period-button:hover,
.period-button:focus {
  background-color: rgba(0, 0, 0, 0.06);
  border-color: var(--color-burgundy);
}

.period-button:focus {
  outline: 2px solid var(--color-burgundy);
  outline-offset: 2px;
}

.period-button.active {
  background-color: var(--color-burgundy);
  border-color: var(--color-burgundy);
  color: white;
  font-weight: 600;
}

.period-select {
  padding: var(--spacing-xs) var(--spacing-sm);
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  font-size: 0.8rem;
  background: var(--color-surface-primary);
  color: var(--color-black-primary);
}

.stats-summary {
  display: flex;
  gap: var(--spacing-lg);
}

.stat-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  text-decoration: none;
  transition: opacity var(--duration-fast) ease;
}

.stat-item:hover {
  opacity: 0.7;
}

.stat-value {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--color-burgundy);
  font-family: var(--font-serif);
}

.stat-label {
  font-size: 0.7rem;
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

/* Mobile select hidden on desktop, buttons hidden on mobile */
.mobile-only { display: none; }

@media (max-width: 768px) {
  .archive-nav {
    padding: 0 var(--spacing-sm);
  }

  .nav-row-primary {
    flex-direction: column;
  }

  .wing-tabs {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  .wing-tabs::-webkit-scrollbar {
    display: none;
  }

  .wing-tab {
    padding: var(--spacing-sm) var(--spacing-md);
    font-size: 0.8rem;
  }

  .nav-divider {
    width: auto;
    height: 1px;
    margin: 0;
  }

  .personal-links {
    justify-content: center;
  }

  .personal-link {
    padding: var(--spacing-sm) var(--spacing-md);
    font-size: 0.8rem;
  }

  .nav-row-secondary {
    flex-direction: column;
    gap: var(--spacing-sm);
    align-items: flex-start;
  }

  .desktop-only { display: none; }
  .mobile-only { display: block; }

  .stats-summary {
    width: 100%;
    justify-content: space-around;
  }
}
</style>
